/* ==========================================================================
   macaje-story.js
   Behaviour for the three motion-led sections. Vanilla, no dependencies.

     <macaje-marquee>     measures the ticker so its speed is constant
     <macaje-journey>     drives the scroll-linked progress rail
     <macaje-stat-rings>  draws the rings and counts the numbers up

   Same custom-element approach as macaje-proof.js: connectedCallback runs
   whenever the element enters the document, so a theme-editor re-render over
   the Section Rendering API rebinds automatically.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function num(el, name, fallback) {
    var raw = parseFloat(el.getAttribute(name));
    return isNaN(raw) ? fallback : raw;
  }

  /**
   * Vertical distance from `root`'s padding edge to `el`'s border edge, in
   * layout coordinates.
   *
   * Deliberately NOT getBoundingClientRect(). A rect is a *visual* box: it
   * includes every transform on the element and its ancestors. The journey's
   * steps carry `translate: 0 var(--ms-step-rise)` while they are unlit, so a
   * rect-measured marker reads ~8px lower than it actually sits — and since the
   * stops are cached, that error is baked in permanently and parks the gold disc
   * 8px below the icon it is supposed to be centred on.
   *
   * offsetTop is the layout position and ignores transforms entirely, so the
   * measurement is the same whether the step is mid-reveal, lit, or dim. The
   * trade is that offsets are integers: up to half a pixel of rounding, against
   * eight pixels of drift.
   *
   * Returns null if the chain does not reach `root` (e.g. the element is
   * display:none and has no offsetParent), so callers can skip rather than
   * position against a bogus number.
   */
  function layoutTop(el, root) {
    var y = 0;
    var node = el;
    while (node && node !== root) {
      y += node.offsetTop;
      node = node.offsetParent;
    }
    return node === root ? y : null;
  }

  /* ================================================================= marquee */

  /**
   * Builds a genuinely gapless ticker, and keeps its *speed* constant.
   *
   * The Liquid renders ONE group (the real, accessible copy of the list). This
   * element then clones that group until the strip is at least one full group
   * wider than the bar, and shifts the whole track by exactly one group's
   * measured width per loop. Both parts are load-bearing:
   *
   *   too few copies  → the tail runs out mid-loop and you see empty bar
   *   wrong distance  → the restart jumps, because the copies do not land on
   *                     the positions of the ones before them
   *
   * The copy count is derived, not assumed, so four short words and twenty long
   * ones are both seamless, at any viewport, after any resize.
   *
   * Speed is pixels-per-second rather than seconds-per-loop for the same
   * reason: the duration is recomputed from the measured group width, so
   * editing the copy changes how long the strip is, never how fast it moves.
   */
  class MacajeMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ms-track]');
      if (!this.track) return;

      // The Liquid-rendered group. Every other group is a clone of this, and it
      // is the only one assistive tech ever sees.
      this.original = this.track.firstElementChild;
      if (!this.original) return;

      this.speed = num(this, 'data-speed', 60); // px per second
      this.build = this.build.bind(this);

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.build);

        // The HOST, because its width is viewport-driven. Never the track:
        // build() changes the track's width itself, which would feed back into
        // an endless loop.
        this.ro.observe(this);

        // The ORIGINAL GROUP as well, and this one matters more than it looks.
        // The strip is first measured in the fallback font; when the webfont
        // swaps in, every label reflows and one group gets ~2px narrower. If
        // the shift distance is still the old number the loop no longer lands
        // copy-on-copy and the seam visibly jumps once per cycle.
        // Observing the group means the swap itself triggers the re-measure,
        // which `document.fonts.ready` does not reliably do — it can resolve
        // before the reflow it is supposed to signal. Safe to observe, because
        // build() only adds siblings after it; it never resizes the original.
        this.ro.observe(this.original);
      } else {
        window.addEventListener('resize', this.build, { passive: true });
        // No ResizeObserver: fall back to the font promise, one frame late so
        // the reflow it announces has actually been applied.
        if (document.fonts && document.fonts.ready) {
          var self = this;
          document.fonts.ready.then(function () {
            requestAnimationFrame(function () { self.build(); });
          });
        }
      }

      this.build();

      // Re-measure a few times after first layout, and treat this as the
      // primary correctness mechanism rather than a nicety.
      //
      // The strip is first laid out in the fallback font. When Jost swaps in,
      // every label reflows and one group loses ~2px. If --ms-shift still holds
      // the pre-swap number the loop stops landing copy-on-copy and the seam
      // jumps ~2px once per cycle — small, but it reads as a stutter.
      //
      // Neither `document.fonts.ready` nor a ResizeObserver on the group can be
      // relied on for this: the promise can resolve before the reflow it
      // announces, and the observer was measured not firing for a pure
      // font-metric change here. Three cheap re-measures at widening intervals
      // cover the swap whenever it lands; build() is idempotent, so a rebuild
      // that finds nothing changed costs one measurement and rewrites the same
      // values.
      requestAnimationFrame(this.build);
      window.addEventListener('load', this.build, { once: true });
      this.settleTimer = setTimeout(this.build, 400);
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      if (this.settleTimer) clearTimeout(this.settleTimer);
      window.removeEventListener('resize', this.build);
      window.removeEventListener('load', this.build);
    }

    build() {
      if (!this.track || !this.original || !this.speed) return;

      // Drop previous clones first, so the measurement below is of one group
      // rather than of the whole strip built on the last pass.
      while (this.track.children.length > 1) {
        this.track.lastElementChild.remove();
      }

      var groupWidth = this.original.getBoundingClientRect().width;
      var hostWidth = this.getBoundingClientRect().width;
      if (!groupWidth || !hostWidth) return;

      // One group to cover the bar, plus one more so there is always content
      // queued past the right edge for the moment the loop restarts.
      var copies = Math.max(2, Math.ceil(hostWidth / groupWidth) + 1);

      var frag = document.createDocumentFragment();
      for (var i = 1; i < copies; i++) {
        var clone = this.original.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        // Clones must not carry the theme editor's block bindings, or selecting
        // a block in the editor would highlight several elements at once.
        clone.removeAttribute('id');
        var tagged = clone.querySelectorAll('[data-shopify-editor-block], [id]');
        for (var j = 0; j < tagged.length; j++) {
          tagged[j].removeAttribute('data-shopify-editor-block');
          tagged[j].removeAttribute('id');
        }
        frag.appendChild(clone);
      }
      this.track.appendChild(frag);

      // Travel exactly one group, and take groupWidth / speed seconds to do it.
      this.track.style.setProperty('--ms-shift', groupWidth.toFixed(2) + 'px');
      this.track.style.setProperty('--ms-bar-duration', (groupWidth / this.speed).toFixed(2) + 's');
    }
  }

  /* ================================================================= journey */

  /**
   * Maps the section's scroll position to a 0–1 progress value on the rail, and
   * lights each step as the line passes its marker.
   *
   * Reads happen inside a rAF tick driven by a passive scroll listener, and the
   * only writes are two custom properties — so the browser animates the fill
   * height and head position itself rather than JS animating anything.
   */
  class MacajeJourney extends HTMLElement {
    connectedCallback() {
      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-ms-step]'));
      this.railWrap = this.querySelector('[data-ms-rail]');
      if (!this.steps.length) return;

      this.start = num(this, 'data-start', 0.75); // viewport fraction where fill begins
      this.end = num(this, 'data-end', 0.35); // …and where it completes
      this.snap = this.getAttribute('data-snap') !== 'false';
      this.hyst = num(this, 'data-hysteresis', 0.02);
      this.index = -1; // active step; -1 until the first update
      this.ticking = false;

      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);

      if (REDUCED.matches) {
        // Show the finished state: full rail, every step lit.
        this.style.setProperty('--ms-progress', '1');
        var rh = this.railWrap && this.railWrap.offsetHeight;
        if (rh) this.style.setProperty('--ms-offset', rh.toFixed(2) + 'px');
        var last = this.steps.length - 1;
        this.steps.forEach(function (s, i) {
          s.setAttribute('data-lit', 'true');
          s.setAttribute('data-current', i === last ? 'true' : 'false');
        });
        return;
      }

      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize, { passive: true });

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();

      // Stop positions are measured from layout, and layout keeps moving after
      // first paint: the webfont swaps and reflows every step's body copy, and
      // the media tiles load. A stop cached from that first measurement leaves
      // the disc parked off its icon.
      //
      // Re-measure on a widening schedule, the same approach the marquee uses
      // for the same underlying reason. (The reveal transform used to land here
      // too, but that was a measurement bug rather than a timing one — see
      // layoutTop(); it is fixed at the source and no longer needs outrunning.)
      this.settle = this.settle.bind(this);
      requestAnimationFrame(this.settle);
      window.addEventListener('load', this.settle, { once: true });
      this.settleTimer = setTimeout(this.settle, 400);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.settle) window.removeEventListener('load', this.settle);
      if (this.settleTimer) clearTimeout(this.settleTimer);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    /**
     * Drop the measured stops and re-apply.
     *
     * Resetting `index` matters as much as clearing the cache: update() only
     * writes --ms-progress when the active step CHANGES, so without this a
     * corrected stop for the step we are already on would never be applied.
     */
    settle() {
      this.cachedStops = null;
      this.index = -1;
      this.update();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      var self = this;
      this.rafId = requestAnimationFrame(function () {
        self.ticking = false;
        self.update();
      });
    }

    onResize() {
      // Stop positions are measured from layout, so they must be recomputed
      // when the layout changes.
      this.cachedStops = null;
      this.onScroll();
    }

    /**
     * Where each step sits on the rail, as a 0–1 fraction of the rail's height.
     *
     * Measured from each marker's centre rather than assumed to be evenly
     * spaced, because steps have different amounts of body copy and therefore
     * different heights. Even spacing would park the head slightly above or
     * below the icons; measuring puts it exactly on them.
     */
    stops() {
      if (this.cachedStops) return this.cachedStops;
      if (!this.railWrap) return null;

      var railTop = layoutTop(this.railWrap, this);
      var railH = this.railWrap.offsetHeight;
      if (railTop === null || !railH) return null;

      // Kept so the indicator can be positioned in PIXELS from the very same
      // measurement that produced the stops. A percentage would have to be
      // re-resolved by the browser against a containing block, which is one
      // more place for the disc and the icon to disagree; sharing one number in
      // one unit makes them coincide by construction.
      this.railH = railH;
      var out = [];
      for (var i = 0; i < this.steps.length; i++) {
        var marker = this.steps[i].querySelector('[data-ms-marker]') || this.steps[i];
        var top = layoutTop(marker, this);
        if (top === null) return null;
        // The head's containing block is the rail wrap, so every stop is
        // expressed relative to the wrap's top — the same origin the CSS uses.
        var f = (top + marker.offsetHeight / 2 - railTop) / railH;
        out.push(Math.max(0, Math.min(1, f)));
      }
      this.cachedStops = out;
      return out;
    }

    update() {
      var box = this.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;

      // Continuous scroll position: 0 when the section's top is at `start` of
      // the viewport, 1 when its bottom has risen to `end`.
      var from = vh * this.start;
      var to = vh * this.end;
      var span = box.height + (from - to);
      var travelled = from - box.top;
      var p = span > 0 ? travelled / span : 0;
      p = Math.max(0, Math.min(1, p));

      var stops = this.stops();

      // ---- Continuous mode: the rail tracks the scroll exactly. ----
      if (!this.snap || !stops) {
        this.style.setProperty('--ms-progress', p.toFixed(4));
        if (!stops) return;
        this.style.setProperty('--ms-offset', (p * this.railH).toFixed(2) + 'px');
        var reached = -1;
        for (var c = 0; c < this.steps.length; c++) {
          var lit = p >= stops[c];
          this.steps[c].setAttribute('data-lit', lit ? 'true' : 'false');
          if (lit) reached = c;
        }
        for (var d = 0; d < this.steps.length; d++) {
          this.steps[d].setAttribute('data-current', d === reached ? 'true' : 'false');
        }
        return;
      }

      // ---- Snap mode: quantise to whichever step is nearest. ----
      //
      // The rail is never given the raw scroll value; it is given one of the
      // measured stop positions, so the head can only ever rest on an icon.
      // The CSS transition on the fill and the head is what turns each change
      // of index into a visible glide rather than a jump — the scroll chooses
      // WHICH step, and CSS animates the travel to it.
      var best = 0;
      var bestDelta = Infinity;
      for (var i = 0; i < stops.length; i++) {
        var d = Math.abs(p - stops[i]);
        if (d < bestDelta) {
          bestDelta = d;
          best = i;
        }
      }

      // Hysteresis: right on the midpoint between two stops, sub-pixel scroll
      // jitter would otherwise flip the active step back and forth every frame.
      // The new step has to be clearly nearer than the current one before it
      // takes over.
      if (this.index >= 0 && best !== this.index) {
        var current = Math.abs(p - stops[this.index]);
        if (current - bestDelta < this.hyst) best = this.index;
      }

      if (best !== this.index) {
        this.index = best;
        this.style.setProperty('--ms-progress', stops[best].toFixed(4));
        this.style.setProperty('--ms-offset', (stops[best] * this.railH).toFixed(2) + 'px');
        for (var j = 0; j < this.steps.length; j++) {
          this.steps[j].setAttribute('data-lit', j <= best ? 'true' : 'false');
          // `data-lit` is true for every step up to and including the active
          // one, so it cannot say which single step the disc is sitting on.
          // `data-current` marks exactly that one — it is what turns the icon
          // white, and what turns the previous one back to gold.
          this.steps[j].setAttribute('data-current', j === best ? 'true' : 'false');
        }
        this.setAttribute('data-step', String(best + 1));
      }
    }
  }

  /* =================================================================== stats */

  /**
   * Draws each ring and counts its number up the first time the row is seen.
   *
   * The ring itself is pure CSS (a dashoffset transition keyed off
   * [data-inview]); the only thing JS animates is the text, because there is no
   * declarative way to interpolate a number's *content*. That counter is
   * rAF-driven and eased to match the ring's curve, so the two finish together.
   */
  class MacajeStatRings extends HTMLElement {
    connectedCallback() {
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-ms-stat]'));
      if (!this.cards.length) return;

      this.duration = num(this, 'data-duration', 1500);
      this.count = this.getAttribute('data-count') === 'true';

      if (!('IntersectionObserver' in window) || REDUCED.matches) {
        this.play(true);
        return;
      }

      var self = this;
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            self.play(false);
            self.io.disconnect();
          });
        },
        { threshold: num(this, 'data-threshold', 0.4) }
      );
      this.io.observe(this);

      // Theme editor: replay when the merchant selects the section.
      this.addEventListener('shopify:section:select', function () {
        self.reset();
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { self.play(false); });
        });
      });
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      this.cards.forEach(function (c) { if (c.__raf) cancelAnimationFrame(c.__raf); });
    }

    reset() {
      this.cards.forEach(function (card) {
        card.setAttribute('data-inview', 'false');
        var v = card.querySelector('[data-ms-value]');
        if (v) v.textContent = v.dataset.prefix + '0' + v.dataset.suffix;
      });
    }

    play(instant) {
      var self = this;
      this.cards.forEach(function (card, i) {
        card.setAttribute('data-inview', 'true');
        var v = card.querySelector('[data-ms-value]');
        if (!v) return;

        var target = parseFloat(v.dataset.value) || 0;
        var decimals = (v.dataset.value || '').split('.')[1] ? 1 : 0;
        var write = function (n) {
          v.textContent = v.dataset.prefix + n.toFixed(decimals) + v.dataset.suffix;
        };

        if (instant || !self.count) {
          write(target);
          return;
        }

        var delay = parseFloat(card.style.getPropertyValue('--ms-delay')) || 0;
        var startAt = null;
        var dur = self.duration;

        var tick = function (now) {
          if (startAt === null) startAt = now;
          var t = (now - startAt - delay) / dur;
          if (t < 0) { card.__raf = requestAnimationFrame(tick); return; }
          if (t >= 1) { write(target); return; }
          // Matches the ring's cubic-bezier(.22,.61,.36,1) closely enough that
          // the number and the arc land on the same frame.
          var eased = 1 - Math.pow(1 - t, 3);
          write(target * eased);
          card.__raf = requestAnimationFrame(tick);
        };

        write(0);
        card.__raf = requestAnimationFrame(tick);
      });
    }
  }

  /* ---------------------------------------------------------------- register */

  if (!customElements.get('macaje-marquee')) {
    customElements.define('macaje-marquee', MacajeMarquee);
  }
  if (!customElements.get('macaje-journey')) {
    customElements.define('macaje-journey', MacajeJourney);
  }
  if (!customElements.get('macaje-stat-rings')) {
    customElements.define('macaje-stat-rings', MacajeStatRings);
  }
})();
